import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { SelectButton } from 'primereact/selectbutton';

export const MAIL_PLACEHOLDERS = {
    NuevoGasto: [
        { clave: 'barrio', descripcion: 'Nombre del barrio' },
        { clave: 'periodo', descripcion: 'Período del gasto' },
        { clave: 'detalle', descripcion: 'Detalle del gasto' },
        { clave: 'comprobante', descripcion: 'Número de comprobante' },
        { clave: 'total', descripcion: 'Total del gasto' },
    ],
    EnvioMail: [
        { clave: 'barrio', descripcion: 'Nombre del barrio' },
        { clave: 'periodo', descripcion: 'Período de la liquidación' },
        { clave: 'nombrePersona', descripcion: 'Nombre del destinatario' },
    ],
    NuevaInfraccion: [
        { clave: 'barrio', descripcion: 'Nombre del barrio' },
        { clave: 'unidadFuncional', descripcion: 'Unidad funcional infraccionada' },
        { clave: 'nombrePersona', descripcion: 'Nombre del destinatario' },
        { clave: 'motivo', descripcion: 'Motivo de la infracción' },
        { clave: 'monto', descripcion: 'Monto de la infracción' },
        { clave: 'fecha', descripcion: 'Fecha de la infracción' },
    ],
    LiquidacionAdministracion: [
        { clave: 'administracion', descripcion: 'Nombre de la administradora' },
        { clave: 'periodo', descripcion: 'Período de la liquidación' },
        { clave: 'total', descripcion: 'Total a liquidar' },
    ],
};

const SAMPLE_DATA = {
    NuevoGasto: { barrio: 'Mi Barrio', periodo: 'Marzo 2026', detalle: 'Mantenimiento de espacios comunes', comprobante: 'FC-0001-00001234', total: '$150.000,00' },
    EnvioMail: { barrio: 'Mi Barrio', periodo: 'Marzo 2026', nombrePersona: 'Juan Pérez' },
    NuevaInfraccion: { barrio: 'Mi Barrio', unidadFuncional: 'Lote 12', nombrePersona: 'Juan Pérez', motivo: 'Ruidos molestos fuera de horario', monto: '$25.000,00', fecha: '10/03/2026' },
    LiquidacionAdministracion: { administracion: 'Zaputovich', periodo: 'Marzo 2026', total: '$1.250.000,00' },
};

const TAMANIOS_LOGO = [
    { label: 'Chico', value: 52 },
    { label: 'Mediano', value: 78 },
    { label: 'Grande', value: 117 },
];

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
    ],
};

// Editor de templates de mail (asunto + HTML) reutilizado tanto en la pantalla de
// Configuración de Mails como en el popup de "Enviar por mail" del Dashboard.
const MailTemplateEditor = ({
    tipoMail,
    asunto,
    onChangeAsunto,
    htmlTemplate,
    onChangeHtml,
    barrioNombre,
    esDefault = false,
    onError,
}) => {
    const quillRef = useRef(null);
    const editorWrapperRef = useRef(null);
    const hideImgBotonTimer = useRef(null);

    const [logoSize, setLogoSize] = useState(78);
    const [imgHover, setImgHover] = useState(null); // { node, top, left }
    const [previewVisible, setPreviewVisible] = useState(false);

    // Muestra un botón "×" flotante sobre cualquier imagen del editor al pasar el mouse, para poder borrarla fácil.
    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        const wrapper = editorWrapperRef.current;
        if (!editor || !wrapper) return undefined;

        const mostrarBoton = (imgNode) => {
            if (hideImgBotonTimer.current) {
                clearTimeout(hideImgBotonTimer.current);
                hideImgBotonTimer.current = null;
            }
            const wrapperRect = wrapper.getBoundingClientRect();
            const imgRect = imgNode.getBoundingClientRect();
            setImgHover({
                node: imgNode,
                top: imgRect.top - wrapperRect.top,
                left: imgRect.right - wrapperRect.left,
            });
        };

        const ocultarBotonConDelay = () => {
            hideImgBotonTimer.current = setTimeout(() => setImgHover(null), 200);
        };

        const handleOver = (e) => { if (e.target.tagName === 'IMG') mostrarBoton(e.target); };
        const handleOut = (e) => { if (e.target.tagName === 'IMG') ocultarBotonConDelay(); };

        editor.root.addEventListener('mouseover', handleOver);
        editor.root.addEventListener('mouseout', handleOut);
        return () => {
            editor.root.removeEventListener('mouseover', handleOver);
            editor.root.removeEventListener('mouseout', handleOut);
            if (hideImgBotonTimer.current) clearTimeout(hideImgBotonTimer.current);
        };
    }, []);

    const handleEliminarImagen = (imgNode) => {
        const editor = quillRef.current?.getEditor();
        if (!editor || !imgNode) return;
        const blot = ReactQuill.Quill.find(imgNode);
        if (blot) {
            const index = editor.getIndex(blot);
            editor.deleteText(index, 1, 'user');
            onChangeHtml(editor.root.innerHTML);
        }
        setImgHover(null);
    };

    const placeholdersDisponibles = useMemo(() => MAIL_PLACEHOLDERS[tipoMail] || [], [tipoMail]);

    const previewHtml = useMemo(() => {
        let html = htmlTemplate || '';
        const sample = { ...SAMPLE_DATA[tipoMail], barrio: barrioNombre || SAMPLE_DATA[tipoMail]?.barrio };
        Object.entries(sample).forEach(([clave, valor]) => {
            html = html.split(`{{${clave}}}`).join(valor);
        });
        return html;
    }, [htmlTemplate, tipoMail, barrioNombre]);

    const insertarEnCursor = (contenido, tipo = 'text') => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        if (tipo === 'html') {
            editor.clipboard.dangerouslyPasteHTML(index, contenido, 'user');
            editor.setSelection(index + 1);
        } else {
            editor.insertText(index, contenido, 'user');
            editor.setSelection(index + contenido.length);
        }
        onChangeHtml(editor.root.innerHTML);
    };

    const handleInsertarPlaceholder = (clave) => {
        insertarEnCursor(`{{${clave}}}`);
    };

    const handleInsertarLogo = async () => {
        try {
            const logo = localStorage.getItem('administracionLogo') || 'expensas360.png';
            const response = await fetch(`assets/layout/images/administradoras/${logo}`);
            const blob = await response.blob();
            const dataUri = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            // Quill descarta el atributo "style" de las imágenes al insertarlas (su blot de imagen
            // solo conserva alt/height/width/src) — por eso el tamaño hay que fijarlo con el atributo
            // "height" en vez de CSS. Además es más compatible con clientes de mail que ignoran <style>.
            const imgHtml = `<img src="${dataUri}" alt="Logo" height="${logoSize}" />`;
            insertarEnCursor(imgHtml, 'html');
        } catch (err) {
            console.error(err);
            onError?.('No se pudo insertar el logo');
        }
    };

    return (
        <>
            {esDefault && (
                <Tag severity="info" value="Usando plantilla predeterminada — todavía no se guardó una personalizada para este barrio" className="mb-3" />
            )}

            <div className="w-full mt-3 mb-3">
                <label>Asunto</label>
                <InputText
                    value={asunto}
                    onChange={(e) => onChangeAsunto(e.target.value)}
                    className="w-full"
                />
            </div>

            <div className="w-full mb-3">
                <label>Variables disponibles (clic para insertar)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                    {placeholdersDisponibles.map((p) => (
                        <Chip
                            key={p.clave}
                            label={`{{${p.clave}}}`}
                            className="cursor-pointer"
                            onClick={() => handleInsertarPlaceholder(p.clave)}
                            title={p.descripcion}
                        />
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap align-items-center gap-3 mb-2">
                <div className="flex align-items-center gap-2">
                    <label className="m-0">Tamaño del logo</label>
                    <SelectButton value={logoSize} options={TAMANIOS_LOGO} onChange={(e) => e.value !== null && setLogoSize(e.value)} />
                </div>
                <Button label="Insertar logo" icon="pi pi-image" outlined onClick={handleInsertarLogo} type="button" />
                <Button label="Vista previa" icon="pi pi-eye" outlined onClick={() => setPreviewVisible(true)} type="button" />
            </div>
            <small className="block mb-3 text-color-secondary">
                El tamaño se fija al insertar el logo (elegí uno acorde para un mail); si querés cambiarlo, borrá la imagen y volvé a insertarla con otro tamaño.
            </small>

            <div className="mb-4" style={{ position: 'relative' }} ref={editorWrapperRef}>
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={htmlTemplate}
                    onChange={onChangeHtml}
                    modules={QUILL_MODULES}
                    style={{ backgroundColor: '#fff' }}
                />
                {imgHover && (
                    <button
                        type="button"
                        title="Eliminar imagen"
                        onMouseEnter={() => { if (hideImgBotonTimer.current) clearTimeout(hideImgBotonTimer.current); }}
                        onMouseLeave={() => { hideImgBotonTimer.current = setTimeout(() => setImgHover(null), 200); }}
                        onClick={() => handleEliminarImagen(imgHover.node)}
                        style={{
                            position: 'absolute',
                            top: imgHover.top - 10,
                            left: imgHover.left - 10,
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#e11d48',
                            color: '#fff',
                            border: '2px solid #fff',
                            fontSize: 14,
                            lineHeight: '18px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                            padding: 0,
                            zIndex: 10,
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            <Dialog
                header="Vista previa"
                visible={previewVisible}
                style={{ width: '650px' }}
                breakpoints={{ '960px': '100vw' }}
                onHide={() => setPreviewVisible(false)}
            >
                <iframe
                    title="preview-mail"
                    srcDoc={previewHtml}
                    style={{ width: '100%', height: '500px', border: 'none' }}
                />
            </Dialog>
        </>
    );
};

export default MailTemplateEditor;
